import { useEffect, useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "V2.0/common/dialog";
import { CheckIcon } from "@heroicons/react/24/outline";
import { Button } from "V2.0/common/button";
import { Badge } from "V2.0/common/badge";
import { message } from "antd";
import { formatTime, showErrorToast } from "V2.0/utils";
import { getEvalTemplateCopyCandidates } from "./service";

export default EvalTemplateCopy;

function EvalTemplateCopy(props) {
  const { workspaceId, assistantId, onConfirmCopy, onClose } = props;

  const [candidates, setCandidates] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCopying, setIsCopying] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const response = await getEvalTemplateCopyCandidates({
          params: { workspaceId, assistantId },
        });
        if (!cancelled) setCandidates(response.data?.templates ?? []);
      } catch (e) {
        if (!cancelled) setCandidates([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [workspaceId, assistantId]);



  const handleCopy = () => {
    if (!selectedId) return;

    function init() {
      setIsCopying(true);
      message.loading("Copying template");
    }

    function success() {
      setIsCopying(false);
      message.destroy();
      message.success("Template copied to this assistant");
      onClose();
    }

    function error(e) {
      setIsCopying(false);
      message.destroy();
      showErrorToast(e, "Failed to copy template");
    }

    onConfirmCopy(selectedId, { init, success, error });
  };

  return (
    <Dialog open={true} onClose={isCopying ? () => {} : onClose} size="2xl">
      <DialogTitle>Copy a template</DialogTitle>
      <DialogBody>
        <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
          Pick a template from another assistant in this workspace. You get your own
          copy — editing it later won't affect the original. You can rename it
          afterwards under Edit settings.
        </p>

        {isLoading && (
          <div className="space-y-2">
            {[0, 1].map((row) => (
              <div
                key={row}
                className="h-[62px] animate-pulse rounded-lg bg-zinc-100 dark:bg-zinc-800"
              />
            ))}
          </div>
        )}

        {!isLoading && candidates.length === 0 && (
          <div className="rounded-lg border border-dashed border-zinc-300 px-4 py-8 text-center dark:border-zinc-700">
            <p className="text-sm font-medium text-zinc-900 dark:text-white">
              Nothing to copy yet
            </p>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              No other assistant in this workspace has a template. Create one there
              first, then copy it across.
            </p>
          </div>
        )}

        {!isLoading && candidates.length > 0 && (
          <div className="flex max-h-[50vh] flex-col gap-y-2 overflow-y-auto">
            {candidates.map((candidate) => {
              const isSelected = selectedId === String(candidate._id);
              const personas = candidate.config?.personas ?? [];
              const models = candidate.config?.modelsToTest ?? [];

              return (
                <button
                  key={String(candidate._id)}
                  type="button"
                  onClick={() => setSelectedId(String(candidate._id))}
                  aria-pressed={isSelected}
                  className={`group flex items-start gap-x-3 rounded-lg border px-4 py-3 text-left transition ${
                    isSelected
                      ? "border-blue-500 bg-blue-50/70 ring-1 ring-blue-500 dark:border-blue-400 dark:bg-blue-950/30 dark:ring-blue-400"
                      : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 dark:border-zinc-700 dark:hover:border-zinc-600 dark:hover:bg-zinc-800/50"
                  }`}
                >
                  <span
                    className={`mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition ${
                      isSelected
                        ? "border-blue-500 bg-blue-500 dark:border-blue-400 dark:bg-blue-400"
                        : "border-zinc-300 dark:border-zinc-600"
                    }`}
                  >
                    {isSelected && (
                      <CheckIcon className="h-3 w-3 text-white" strokeWidth={3} />
                    )}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-medium text-zinc-900 dark:text-white">
                        {candidate.name}
                      </span>
                      {candidate.assistant?.name && (
                        <Badge color="zinc">{candidate.assistant.name}</Badge>
                      )}
                    </span>

                    {candidate.originTemplate?.name && (
                      <span className="mt-0.5 block text-xs text-zinc-400 dark:text-zinc-500">
                        copied from “{candidate.originTemplate.name}” · edited{" "}
                        {formatTime(candidate.updatedAt)}
                      </span>
                    )}

                    <span className="mt-1 block truncate text-xs text-zinc-500 dark:text-zinc-400">
                      {personas.length}{" "}
                      {personas.length === 1 ? "persona" : "personas"}
                      {personas.length > 0 &&
                        ` · ${personas
                          .map((persona) => persona?.name)
                          .filter(Boolean)
                          .join(", ")}`}
                      {models.length > 0 &&
                        ` · ${models.length} ${
                          models.length === 1 ? "model" : "models"
                        }`}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button onClick={onClose} outline disabled={isCopying}>
          Cancel
        </Button>
        {candidates.length > 0 && (
          <Button
            color="blue"
            onClick={handleCopy}
            disabled={!selectedId || isCopying}
            loading={isCopying}
          >
            Copy Template
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
